// assignmentsApi — typed fetchers for /v1/admin/{tasks/:id/assignments,assignments/*}.
// Backend: apps/tenant-api/src/assignments.ts.
import { apiFetch } from "../../lib/api-client.js";

export type TargetKind = "role" | "officer";

export interface AssignmentRow {
  id: string;
  task_id: string;
  location_id: string;
  route_id: string | null;
  rrule: string;
  duration_minutes: number;
  grace_before_min: number;
  grace_after_min: number;
  /** unix sec */
  valid_from: number;
  valid_to: number | null;
  target_kind: TargetKind;
  target_value: string;
  created_by_user_id: string | null;
  created_at: number;
  status: "active" | "archived";
}

export interface AssignmentBody {
  location_id?: string;
  route_id?: string | null;
  rrule?: string;
  duration_minutes?: number;
  grace_before_min?: number;
  grace_after_min?: number;
  valid_from?: number;
  valid_to?: number | null;
  target_kind?: TargetKind;
  target_value?: string;
  status?: "active" | "archived";
}

export class AssignmentsApiError extends Error {
  status: number; code: string; correlationId: string | null; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.upstreamMessage = upstreamMessage;
  }
}

export function friendlyAssignmentsError(err: unknown): string {
  if (err instanceof AssignmentsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能管理派發。";
    if (err.status === 404) {
      if (err.code === "task_not_found") return "找不到此任務。";
      if (err.code === "location_not_found") return "找不到此地點。";
      if (err.code === "route_not_found") return "找不到此路線。";
      if (err.code === "officer_not_found") return "找不到此巡邏員。";
      return "找不到此派發。";
    }
    if (err.code === "task_archived") return "目標任務已封存，無法新增派發。";
    // `location_not_in_route` 已於 server side 自動處理（auto-extend
    // route_locations），不會再回到 client。保留 fallback 防 server
    // 邏輯被未來 PR 改回 hard reject 而 frontend 沒同步。
    if (err.code === "location_not_in_route") return "此路線沒有經過這個地點。";
    if (err.code === "rrule_required") return "請輸入時間規則 (RRULE)。";
    if (err.code === "rrule_invalid") return "RRULE 格式不正確。";
    if (err.code === "rrule_no_upcoming_occurrences") return "此 RRULE 在未來一年內沒有觸發點，請檢查設定。";
    if (err.code === "duration_out_of_range") return "持續時間需介於 1 ~ 1440 分鐘。";
    if (err.code === "grace_before_out_of_range" || err.code === "grace_after_out_of_range") return "寬限期需介於 0 ~ 1440 分鐘。";
    if (err.code === "valid_range_invalid") return "有效期間：結束時間需晚於開始時間。";
    if (err.code === "location_id_required") return "請選擇一個地點。";
    if (err.code === "valid_from_required") return "請填寫派發生效時間。";
    if (err.code === "target_kind_required" || err.code === "target_value_required") return "請選擇對象（角色或個別巡邏員）。";
    if (err.code === "invalid_target_kind") return "對象類型不正確。";
    if (err.code === "invalid_role_value") return "角色名稱不正確。";
    if (err.code === "tenant_id_required") return "尚未選定操作的單位。";
    return `操作失敗（HTTP ${err.status}・code=${err.code}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<AssignmentsApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let msg: string | undefined;
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string; message?: string };
    code = body.error ?? code; cid = body.correlation_id ?? cid; msg = body.message;
  } catch {}
  return new AssignmentsApiError(r.status, code, cid, msg);
}

function tenantQuery(tenantId: string | null | undefined): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
}

export async function listAssignmentsForTask(taskId: string, tenantId: string | null = null): Promise<{ items: AssignmentRow[] }> {
  const r = await apiFetch(`/v1/admin/tasks/${encodeURIComponent(taskId)}/assignments${tenantQuery(tenantId)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: AssignmentRow[] };
}

/** AssignmentRow shape with task_name denormalised — only returned by
 *  the by-location endpoint so the Location drawer can render the
 *  「Tap 此處會觸發的任務」 list without an N+1 follow-up. */
export interface AssignmentRowWithTask extends AssignmentRow {
  task_name: string | null;
}

export async function listAssignmentsForLocation(
  locationId: string, tenantId: string | null = null,
): Promise<{ items: AssignmentRowWithTask[] }> {
  const r = await apiFetch(
    `/v1/admin/locations/${encodeURIComponent(locationId)}/assignments${tenantQuery(tenantId)}`,
    { credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: AssignmentRowWithTask[] };
}

export async function getAssignment(id: string, tenantId: string | null = null): Promise<AssignmentRow> {
  const r = await apiFetch(`/v1/admin/assignments/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AssignmentRow;
}

/** Server may report `route_extended: true` when the (route, location)
 *  pair didn't exist in route_locations — the assignment write also
 *  appended the location to the route. Frontend uses this to show a
 *  post-save toast confirming the side effect. */
export interface AssignmentSaveResult { ok: true; id: string; route_extended?: boolean }

export async function createAssignment(taskId: string, body: AssignmentBody, tenantId: string | null = null): Promise<AssignmentSaveResult> {
  const r = await apiFetch(`/v1/admin/tasks/${encodeURIComponent(taskId)}/assignments${tenantQuery(tenantId)}`, {
    method: "POST", headers: { "content-type": "application/json" },
    body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AssignmentSaveResult;
}

export async function patchAssignment(id: string, body: AssignmentBody, tenantId: string | null = null): Promise<AssignmentSaveResult> {
  const r = await apiFetch(`/v1/admin/assignments/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, {
    method: "PATCH", headers: { "content-type": "application/json" },
    body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AssignmentSaveResult;
}

export async function archiveAssignment(id: string, tenantId: string | null = null): Promise<{ ok: true }> {
  const r = await apiFetch(`/v1/admin/assignments/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, {
    method: "DELETE", credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true };
}
